/**
 * Header Mega Menu functionality
 * Handles both hover and click interactions for parent menu items
 * Supports both interaction methods simultaneously
 */

class HeaderMegaMenuHover {
  constructor() {
    this.init();
  }

  isMobileDevice() {
    // Check if device is mobile/tablet based on screen size and touch capability
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 1024;

    return isTouchDevice || isSmallScreen;
  }

  init() {
    // Hide all mega menus immediately to prevent flashing
    this.hideAllMegaMenus();

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () =>
        this.setupEventListeners()
      );
    } else {
      this.setupEventListeners();
    }
  }

  hideAllMegaMenus() {
    // Immediately hide all mega menu contents to prevent flashing
    const allMegaMenus = document.querySelectorAll('.mega-menu--hover');
    allMegaMenus.forEach(megamenu => {
      const content = megamenu.querySelector('.mega-menu__content');
      if (content) {
        content.style.display = 'none';
        content.style.opacity = '0';
        content.style.visibility = 'hidden';
        megamenu.classList.remove('mega-menu--active');
        content.classList.remove('mega-menu__content--active');
      }
    });
  }

  setupEventListeners() {
    // Find all hover-based megamenus
    const hoverMegamenus = document.querySelectorAll('.mega-menu--hover');

    hoverMegamenus.forEach(megamenu => {
      this.setupHoverMegamenu(megamenu);
    });
  }

  setupHoverMegamenu(megamenu) {
    const trigger = megamenu.querySelector('.mega-menu__trigger');
    const content = megamenu.querySelector('.mega-menu__content');

    if (!trigger || !content) return;

    // Ensure mega menu is hidden on initialization
    this.hideMegamenu(megamenu, content);

    // Calculate the correct top position for fixed positioning
    // this.calculateMegamenuPosition(trigger, content);

    let hoverTimeout;
    let isOpen = false;

    // HOVER EVENTS (for desktop)
    // Mouse enter on trigger
    trigger.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      this.showMegamenu(megamenu, content);
      isOpen = true;
    });

    // Mouse leave on trigger
    trigger.addEventListener('mouseleave', () => {
      hoverTimeout = setTimeout(() => {
        this.hideMegamenu(megamenu, content);
        isOpen = false;
      }, 100); // Small delay to allow moving to content
    });

    // Mouse enter on content
    content.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      this.showMegamenu(megamenu, content);
      isOpen = true;
    });

    // Mouse leave on content
    content.addEventListener('mouseleave', () => {
      hoverTimeout = setTimeout(() => {
        this.hideMegamenu(megamenu, content);
        isOpen = false;
      }, 100);
    });

    // Mouse enter on entire megamenu container
    megamenu.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      this.showMegamenu(megamenu, content);
      isOpen = true;
    });

    // Mouse leave on entire megamenu container
    megamenu.addEventListener('mouseleave', () => {
      hoverTimeout = setTimeout(() => {
        this.hideMegamenu(megamenu, content);
        isOpen = false;
      }, 100);
    });

    // CLICK EVENTS (for mobile/tablet)
    // Click event on trigger
    trigger.addEventListener('click', e => {
      // Always handle click events, but with different behavior based on device
      if (this.isMobileDevice()) {
        e.preventDefault();
        e.stopPropagation();

        if (isOpen) {
          this.hideMegamenu(megamenu, content);
          isOpen = false;
        } else {
          this.showMegamenu(megamenu, content);
          isOpen = true;
        }
      } else {
        // On desktop, click should also work as a fallback
        if (isOpen) {
          this.hideMegamenu(megamenu, content);
          isOpen = false;
        } else {
          this.showMegamenu(megamenu, content);
          isOpen = true;
        }
      }
    });

    // Touch events for better mobile experience
    trigger.addEventListener(
      'touchstart',
      e => {
        if (this.isMobileDevice()) {
          // Prevent the click event from firing after touch
          e.preventDefault();

          // Handle touch as click
          if (isOpen) {
            this.hideMegamenu(megamenu, content);
            isOpen = false;
          } else {
            this.showMegamenu(megamenu, content);
            isOpen = true;
          }
        }
      },
      { passive: false }
    );

    // Click outside to close (only for mobile/touch devices)
    document.addEventListener('click', e => {
      if (this.isMobileDevice()) {
        if (!megamenu.contains(e.target) && isOpen) {
          this.hideMegamenu(megamenu, content);
          isOpen = false;
        }
      }
    });

    // Recalculate position on window resize
    // window.addEventListener("resize", () => {
    //   this.calculateMegamenuPosition(trigger, content);
    // });

    // Setup hover events for category items in sidebar
    this.setupCategoryHoverEvents(megamenu);
  }

  showMegamenu(megamenu, content) {
    megamenu.classList.add('mega-menu--active');
    content.classList.add('mega-menu__content--active');

    // Ensure content is visible
    content.style.display = 'block';

    // Setup category hover events
    this.setupCategoryHoverEvents(megamenu);

    // Auto-activate the first category in the sidebar
    this.activateFirstCategory(megamenu);
  }

  hideMegamenu(megamenu, content) {
    megamenu.classList.remove('mega-menu--active');
    content.classList.remove('mega-menu__content--active');

    // Ensure content is hidden
    content.style.display = 'none';

    // Reset all category active states when hiding megamenu
    this.resetAllCategoryStates(megamenu);

    // Reset events setup flag to allow re-setup
    const customLayout = megamenu.querySelector('.mega-menu__custom-layout');
    if (customLayout) {
      customLayout.dataset.eventsSetup = 'false';
    }
  }

  calculateMegamenuPosition(trigger, content) {
    const triggerRect = trigger.getBoundingClientRect();
    const headerHeight = document.querySelector('.header')?.offsetHeight || 80;
    const headerBarHeight =
      document.querySelector('.utility-bar')?.offsetHeight || 40;

    // Set the top position to be right below the header
    content.style.top = `${headerHeight + headerBarHeight}px`;
  }

  activateFirstCategory(megamenu) {
    // Find the custom layout (Product menu)
    const customLayout = megamenu.querySelector('.mega-menu__custom-layout');
    if (!customLayout) return;

    // Find the first category item in the sidebar
    const firstCategoryItem = customLayout.querySelector(
      '.mega-menu__category-item:first-child'
    );
    if (!firstCategoryItem) return;

    // Find all category items and contents
    const categoryItems = customLayout.querySelectorAll(
      '.mega-menu__category-item'
    );
    const categoryContents = customLayout.querySelectorAll(
      '.mega-menu__category-content'
    );

    // Remove active state from all items
    this.removeActiveStates(categoryItems, categoryContents);

    // Get the category handle and find corresponding content
    const categoryHandle = firstCategoryItem.dataset.category;
    const firstContent = Array.from(categoryContents).find(
      content => content.dataset.category === categoryHandle
    );

    // Activate the first category
    if (firstContent) {
      this.setActiveCategory(firstCategoryItem, firstContent);
    }
  }

  setActiveCategory(item, content) {
    // Add active class to the category item
    item.classList.add('mega-menu__category-item--active');
    const link = item.querySelector('.mega-menu__category-link');
    if (link) {
      link.classList.add('mega-menu__category-link--active');
    }

    // Show the corresponding content
    if (content) {
      content.classList.add('mega-menu__category-content--active');
      content.style.display = 'block'; // Force display block
    }
  }

  removeActiveStates(items, contents) {
    // Remove active state from all category items
    items.forEach(item => {
      item.classList.remove('mega-menu__category-item--active');
      const link = item.querySelector('.mega-menu__category-link');
      if (link) {
        link.classList.remove('mega-menu__category-link--active');
      }
    });

    // Hide all content areas
    contents.forEach(content => {
      content.classList.remove('mega-menu__category-content--active');
      content.style.display = 'none'; // Force display none
    });
  }

  resetAllCategoryStates(megamenu) {
    // Find the custom layout (Product menu)
    const customLayout = megamenu.querySelector('.mega-menu__custom-layout');
    if (!customLayout) return;

    // Find all category items and contents
    const categoryItems = customLayout.querySelectorAll(
      '.mega-menu__category-item'
    );
    const categoryContents = customLayout.querySelectorAll(
      '.mega-menu__category-content'
    );

    // Remove all active states
    this.removeActiveStates(categoryItems, categoryContents);
  }

  setupCategoryHoverEvents(megamenu) {
    // Find the custom layout (Product menu)
    const customLayout = megamenu.querySelector('.mega-menu__custom-layout');
    if (!customLayout) {
      return;
    }

    // Check if events are already setup
    if (customLayout.dataset.eventsSetup === 'true') {
      return;
    }

    // Find all category items and contents
    const categoryItems = customLayout.querySelectorAll(
      '.mega-menu__category-item'
    );
    const categoryContents = customLayout.querySelectorAll(
      '.mega-menu__category-content'
    );

    // Add both hover and click events to each category item
    categoryItems.forEach(item => {
      const categoryHandle = item.dataset.category;

      // Find content by looking for the div with matching data-category
      const content = Array.from(categoryContents).find(
        content => content.dataset.category === categoryHandle
      );

      if (!content) return;

      // Get the link element
      const link = item.querySelector('.mega-menu__category-link');

      // Add click event directly to the link to ensure navigation works
      if (link) {
        link.addEventListener('click', e => {
          // Allow normal link navigation
          e.stopPropagation(); // Prevent event bubbling to parent handlers
          // Don't prevent default - let the link navigate normally
        });
      }

      // HOVER EVENT (for desktop)
      item.addEventListener('mouseenter', e => {
        e.stopPropagation(); // Prevent event bubbling
        this.switchToCategory(item, content, categoryItems, categoryContents);
      });

      // CLICK EVENT (works on both mobile and desktop)
      item.addEventListener('click', e => {
        // Check if the click is on the link itself
        const isLinkClick = link && link.contains(e.target);

        if (isLinkClick) {
          // Allow the link to navigate to its URL
          // Don't prevent default behavior for link clicks
          e.stopPropagation(); // Prevent event bubbling to parent handlers
          return;
        }

        // For clicks on the item (not the link), switch category content
        if (this.isMobileDevice()) {
          e.preventDefault();
          e.stopPropagation();
        }
        this.switchToCategory(item, content, categoryItems, categoryContents);
      });

      // Touch events for category items
      item.addEventListener(
        'touchstart',
        e => {
          if (this.isMobileDevice()) {
            // Check if the touch is on the link itself
            const isLinkTouch = link && link.contains(e.target);

            if (isLinkTouch) {
              // Allow the link to navigate to its URL
              // Don't prevent default behavior for link touches
              e.stopPropagation(); // Prevent event bubbling to parent handlers
              return;
            }

            // For touches on the item (not the link), switch category content
            e.preventDefault();
            this.switchToCategory(
              item,
              content,
              categoryItems,
              categoryContents
            );
          }
        },
        { passive: false }
      );
    });

    // Mark events as setup
    customLayout.dataset.eventsSetup = 'true';
  }

  switchToCategory(activeItem, activeContent, allItems, allContents) {
    // Remove active state from all items and contents
    this.removeActiveStates(allItems, allContents);

    // Set active state for the selected item and content
    this.setActiveCategory(activeItem, activeContent);
  }
}

// Immediately hide mega menus to prevent flashing
(function () {
  const hideMegaMenus = () => {
    const allMegaMenus = document.querySelectorAll('.mega-menu--hover');
    allMegaMenus.forEach(megamenu => {
      const content = megamenu.querySelector('.mega-menu__content');
      if (content) {
        content.style.display = 'none';
        content.style.opacity = '0';
        content.style.visibility = 'hidden';
      }
    });
  };

  // Run immediately
  hideMegaMenus();

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideMegaMenus);
  }
})();

// Initialize the header mega menu hover when the script loads
new HeaderMegaMenuHover();

// Also initialize when the page is fully loaded (for dynamic content)
window.addEventListener('load', () => {
  new HeaderMegaMenuHover();
});
